import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Input, Segmented, Skeleton, Tooltip, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { PageHeader } from "../shared/page-header";
import { useAgentConfig } from "../shared/queries";
import type { Action } from "../shared/types";
import { UpgradeCard, isPlanGate } from "../shared/upgrade-card";
import { ActionCard } from "./action-card";
import { ActionEditor, type EditorTarget } from "./action-editor";
import { ActionKpis } from "./action-kpis";
import { RunsTable } from "./runs-table";
import { emptyFormValues, formValuesFrom, type TemplateDefaults } from "./schema-model";
import { TemplateDialog } from "./template-dialog";
import { useActionMutations, useActionsData } from "./use-actions";

type Kind = "all" | "builtin" | "custom";

function matches(action: Action, kind: Kind, query: string): boolean {
  if (kind === "builtin" && action.type !== "builtin") return false;
  if (kind === "custom" && action.type === "builtin") return false;
  const needle = query.trim().toLowerCase();
  return !needle || `${action.name} ${action.when_to_use} ${action.tool_name}`.toLowerCase().includes(needle);
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-44 rounded-card" />)}
    </div>
  );
}

function ActionsGrid({ agentId, actions, canManage, onOpen }: { agentId: string; actions: readonly Action[]; canManage: boolean; onOpen: (a: Action) => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { toggle } = useActionMutations(agentId);
  const [kind, setKind] = useState<Kind>("all");
  const [query, setQuery] = useState("");
  const count = (k: Kind) => actions.filter((a) => matches(a, k, "")).length;
  const shown = actions.filter((a) => matches(a, kind, query));
  const onToggle = (action: Action) => {
    toggle.mutate(action, {
      onSuccess: (next) => { toast[next.enabled ? "success" : "info"](t(next.enabled ? "build.actions.card.enabled" : "build.actions.card.disabled", { name: next.name })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <section aria-label={t("build.actions.title")}>
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Segmented
          aria-label={t("build.actions.filter.label")}
          value={kind}
          onValueChange={setKind}
          options={[
            { value: "all", label: t("build.actions.filter.all", { count: count("all") }) },
            { value: "builtin", label: t("build.actions.filter.builtin", { count: count("builtin") }) },
            { value: "custom", label: t("build.actions.filter.custom", { count: count("custom") }) },
          ]}
        />
        <div className="relative w-full sm:w-72">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input type="search" className="pl-9" aria-label={t("build.actions.filter.search")} placeholder={t("build.actions.filter.search")} value={query} onChange={(e) => { setQuery(e.target.value); }} />
        </div>
      </div>
      {shown.length === 0 ? (
        <EmptyState icon="filter" headingLevel={3} title={t("build.actions.empty.noMatch")} description={t("build.actions.empty.noMatchBody")} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((action) => (
            <ActionCard key={action.id} action={action} canToggle={canManage} toggling={toggle.isPending && toggle.variables.id === action.id} onOpen={onOpen} onToggle={onToggle} />
          ))}
        </div>
      )}
    </section>
  );
}

export function ActionsPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agentId = useRouteAgentId() ?? "";
  const agent = useAgentConfig(agentId);
  const permissions = usePermissions(agentId);
  const { actions, stats, runs } = useActionsData(agentId);
  const { createBooking } = useActionMutations(agentId);
  const message = useErrorMessage();
  const canManage = !permissions.readOnly && permissions.can("actions.manage", { agentId });
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<EditorTarget | null>(null);
  const open = (action: Action) => { setTarget({ action, integration: (action.integration ?? "custom_api") as EditorTarget["integration"], title: action.name, initial: formValuesFrom(action) }); };
  const create = (template: TemplateDefaults) => {
    setPicking(false);
    setTarget({ action: null, integration: template.integration, title: template.name, initial: emptyFormValues(template, agent.data?.audiences) });
  };
  const addBooking = () => {
    createBooking.mutate(t("build.actions.templates.bookingName"), {
      onSuccess: (action) => {
        setPicking(false);
        toast.success(t("build.actions.templates.bookingCreated"));
        open(action);
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const newButton = (
    <Button onClick={() => { setPicking(true); }} disabled={!canManage}>
      <Icon name="plus" />
      {t("build.actions.newAction")}
    </Button>
  );
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t("build.actions.title")}
        subtitle={t("build.actions.subtitle")}
        actions={
          <>
            <Button asChild variant="secondary">
              <AppLink href={consoleRoutes.playground(summary.slug, agentId)}>
                <Icon name="messages-square" />
                {t("build.actions.tryPlayground")}
              </AppLink>
            </Button>
            {canManage ? newButton : <Tooltip content={t("build.noPermission")}><span>{newButton}</span></Tooltip>}
          </>
        }
      />
      {actions.isError && isPlanGate(actions.error) ? <UpgradeCard feature={t("build.actions.planFeature")} /> : null}
      {actions.isError && !isPlanGate(actions.error) ? (
        <ErrorState title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void actions.refetch()} retryLabel={t("common.retry")} />
      ) : null}
      {actions.isError ? null : <ActionKpis stats={stats.data} agentName={agent.data?.name ?? ""} />}
      {actions.isPending ? <GridSkeleton /> : null}
      {actions.data?.length === 0 ? (
        <EmptyState icon="zap" title={t("build.actions.empty.title")} description={t("build.actions.empty.body")} action={canManage ? newButton : undefined} />
      ) : null}
      {actions.data && actions.data.length > 0 ? <ActionsGrid agentId={agentId} actions={actions.data} canManage={canManage} onOpen={open} /> : null}
      {actions.isError ? null : <RunsTable agentId={agentId} runs={runs.data} isPending={runs.isPending} />}
      <TemplateDialog open={picking} onOpenChange={setPicking} onPick={create} onPickBooking={addBooking} bookingPending={createBooking.isPending} />
      {target ? <ActionEditor key={target.action?.id ?? target.title} agentId={agentId} target={target} canManage={canManage} onClose={() => { setTarget(null); }} /> : null}
    </div>
  );
}
